"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { config } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bot, LogOut, ChevronDown, Menu, KeyRound, Search } from "lucide-react";

const NAV = [
  { href: "/", key: "productos" },
  { href: "/conjuntos", key: "conjuntos" },
  { href: "/componentes", key: "componentes" },
  { href: "/compras", key: "compras" },
  { href: "/ecn", key: "ecn" },
  { href: "/clientes", key: "clientes" },
  { href: "/proyectos", key: "proyectos" },
] as const;

// Solo admin: desplegable "Ajustes".
const AJUSTES = [
  { href: "/ajustes/usuarios", key: "usuarios" },
  { href: "/ajustes/distribuidores", key: "distribuidores" },
  { href: "/ajustes/ecn", key: "casillasEcn" },
  { href: "/ajustes/auditoria", key: "auditoria" },
] as const;

// "/" (Productos) también se resalta dentro de /productos/*, /versiones/*, /boms/* y /comparar.
function activo(href: string, pathname: string) {
  if (href === "/")
    return ["/productos", "/versiones", "/boms", "/comparar"].some((p) => pathname.startsWith(p)) || pathname === "/";
  return pathname.startsWith(href);
}

export function Navbar() {
  const t = useTranslations("nav");
  const { user, loading, logout, changePassword, esAdmin } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [pwOpen, setPwOpen] = useState(false);
  const initials = user ? user.nombre.slice(0, 2).toUpperCase() : "";
  // Admin de arranque / usuario con contraseña temporal: debe cambiarla antes de seguir.
  const forced = user?.debeCambiarPassword ?? false;

  const renderLink = (item: { href: string; key: string }) => (
    <Link
      key={item.href}
      href={item.href}
      className={
        "rounded-md px-2.5 py-1.5 text-sm transition-colors " +
        (activo(item.href, pathname)
          ? "bg-accent text-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-accent/60")
      }
    >
      {t(item.key)}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/70 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <div className="flex items-center gap-6">
          <Link href="/" className="flex items-center gap-2 font-semibold tracking-tight">
            {/* eslint-disable-next-line @next/next/no-img-element -- SVG estático: next/image no aporta nada */}
            <img src="/icon.svg" alt="" className="h-7 w-7" />
            <span className="hidden sm:inline">{config.appName}</span>
          </Link>
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex cursor-pointer items-center rounded-md p-2 text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground md:hidden">
                <Menu className="h-5 w-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {NAV.map((item) => (
                  <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                    {t(item.key)}
                  </DropdownMenuItem>
                ))}
                {esAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    {AJUSTES.map((item) => (
                      <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                        {t(item.key)}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {user && (
            <nav className="hidden items-center gap-1 md:flex">
              {NAV.map(renderLink)}
              {esAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className={
                      "inline-flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 text-sm outline-none transition-colors " +
                      (pathname.startsWith("/ajustes")
                        ? "bg-accent text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/60")
                    }
                  >
                    {t("ajustes")}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {AJUSTES.map((item) => (
                      <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                        {t(item.key)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2">
          {user && (
            <form
              className="relative hidden lg:block"
              onSubmit={(e) => {
                e.preventDefault();
                const q = new FormData(e.currentTarget).get("q")?.toString().trim();
                router.push(q ? `/buscar?q=${encodeURIComponent(q)}` : "/buscar");
              }}
            >
              <Search className="absolute top-2 left-2.5 size-4 text-muted-foreground" />
              <Input name="q" className="w-56 pl-8" placeholder={t("buscar")} />
            </form>
          )}
          {user && (
            <Link href="/buscar" className="rounded-md p-2 text-muted-foreground hover:bg-accent lg:hidden" aria-label={t("buscar")}>
              <Search className="size-5" />
            </Link>
          )}
          {loading ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex cursor-pointer items-center gap-2 rounded-lg p-1.5 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring/50 data-[popup-open]:bg-accent">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-xs font-medium text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-sm font-medium sm:inline">{user.nombre}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="font-medium">{user.nombre}</span>
                    <span className="text-xs font-normal text-muted-foreground">{user.email}</span>
                  </DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <p className="mb-1.5 text-xs text-muted-foreground">{t("roles")}</p>
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map((r) => (
                      <span
                        key={r}
                        className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
                      >
                        {t(`rol.${r}`)}
                      </span>
                    ))}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t("cambiarPassword")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push("/ajustes/tokens-api")}>
                  <Bot className="mr-2 h-4 w-4" />
                  {t("tokensApi")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    // Navegación DURA: descarta todo el estado en memoria de la sesión anterior.
                    await logout();
                    window.location.assign("/login");
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("cerrarSesion")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : pathname !== "/login" ? (
            <Button onClick={() => router.push("/login")}>{t("iniciarSesion")}</Button>
          ) : null}
        </div>
      </div>

      <ChangePasswordDialog
        open={pwOpen || forced}
        forced={forced}
        onOpenChange={setPwOpen}
        changePassword={changePassword}
      />
    </header>
  );
}

function ChangePasswordDialog({
  open,
  forced,
  onOpenChange,
  changePassword,
}: {
  open: boolean;
  forced: boolean;
  onOpenChange: (o: boolean) => void;
  changePassword: (current: string, next: string) => Promise<void>;
}) {
  const t = useTranslations("nav");
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetir, setRepetir] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // Cada apertura empieza limpia (Base UI conserva el árbol al cerrar). En modo forzado (admin de
  // arranque) NO se puede cerrar: ignoramos los intentos de cierre hasta que cambie la contraseña.
  const cambiarOpen = (o: boolean) => {
    if (!o && forced) return;
    if (!o) {
      setActual("");
      setNueva("");
      setRepetir("");
      setError(null);
      setOk(false);
      setBusy(false);
    }
    onOpenChange(o);
  };

  const guardar = async () => {
    setError(null);
    if (nueva.length < 8) return setError(t("passwordMin"));
    if (nueva !== repetir) return setError(t("passwordNoCoincide"));
    setBusy(true);
    try {
      await changePassword(actual, nueva);
      setOk(true);
      setActual("");
      setNueva("");
      setRepetir("");
    } catch (e) {
      setError(e instanceof ApiError ? e.body?.message ?? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={cambiarOpen}>
      <DialogContent showCloseButton={!forced}>
        <DialogHeader>
          <DialogTitle>{t("cambiarPasswordTitulo")}</DialogTitle>
          {forced && (
            <DialogDescription>{t("cambiarPasswordForzado")}</DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pw-actual">{t("passwordActual")}</Label>
            <Input
              id="pw-actual"
              type="password"
              autoComplete="current-password"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw-nueva">{t("passwordNueva")}</Label>
            <Input
              id="pw-nueva"
              type="password"
              autoComplete="new-password"
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw-repetir">{t("passwordRepetir")}</Label>
            <Input
              id="pw-repetir"
              type="password"
              autoComplete="new-password"
              value={repetir}
              onChange={(e) => setRepetir(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {ok && <p className="text-sm text-emerald-600">{t("passwordCambiada")}</p>}
        </div>
        <DialogFooter>
          {!forced && (
            <Button variant="ghost" onClick={() => cambiarOpen(false)}>
              {t("cancelar")}
            </Button>
          )}
          <Button onClick={guardar} disabled={busy}>
            {busy ? t("guardando") : t("guardar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
